import { useCallback, useEffect, useState } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';
import { SignaturesIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import { useSignatureTracking } from '../../hooks/tracking/signature';

import { ActionButtonWrapper } from './ActionButtonWrapper';
import type { ActionPanelSunriseVariant } from './MobileActionButton';
import { MobileSignModal, usePreloadMobileSignModal } from './MobileSignModal';
import { useMobileModalContext } from './shared/useMobileModalContext';
import { useShowSignForm } from './shared/useShowSignForm';

type Props = {
	sticky: boolean;
	visible: boolean;
	variant: ActionPanelSunriseVariant;
};

export const MobileSignButtonContainer: React.FC<Props> = ({ sticky, visible, variant }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { trackMobileSignIntent } = useSignatureTracking();

	const context = useMobileModalContext();

	usePreloadMobileSignModal();

	const openModal = useCallback(() => {
		trackMobileSignIntent();
		setIsOpen(true);
	}, [trackMobileSignIntent]);

	const openModalWithoutTracking = useCallback(() => {
		setIsOpen(true);
	}, []);

	return (
		<>
			{/* we're looking at breakpoints to decide whether to open the modal, which cannot be done server-side */}
			{/* Only auto-open on the non-sticky instance to avoid opening modal twice (both sticky and non-sticky render this component) */}
			{!sticky && (
				<ClientRender>
					<MobileSignButtonAutoOpen onOpen={openModalWithoutTracking} />
				</ClientRender>
			)}
			{/* eslint-disable-next-line @change-corgi/require-data-qa-attribute */}
			<ActionButtonWrapper visible={visible} sticky={sticky}>
				<Button
					variant="primary"
					onClick={openModal}
					leadingIcon={<SignaturesIcon size="small" />}
					data-qa="mobile-sign-button"
					data-testid="mobile-sign-button"
					data-gtm="ga4-sign-button"
					size="large"
					className="w-full max-w-none"
				>
					<div className="flex place-content-center place-items-center gap-3">
						<Translate value="corgi.petition_gamma.petition_sign.action" />
					</div>
				</Button>
			</ActionButtonWrapper>

			<MobileSignModal context={context} isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</>
	);
};
function MobileSignButtonAutoOpen({ onOpen }: { onOpen: () => void }) {
	const showSignForm = useShowSignForm();

	useEffect(() => {
		if (showSignForm) {
			/**
			 * There are some updates that happen right before showing the modal (such as populating the form fields)
			 * that need to complete first so that the modal opens with the correct data.
			 * Using setTimeout to defer the onOpen call to the next event loop iteration and avoid a race condition.
			 */
			setTimeout(() => {
				onOpen();
			}, 0);
		}
		// disabling dependency check to avoid attempting to open the modal multiple times
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
}
